import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  OnInit,
  output,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormArray, FormBuilder, FormControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms'
import { LocalTimeRange } from '@ftr/contracts/type/shared'
import {
  isFilledOut,
  isTimeRangeControl,
  RadioItem,
  RadioListComponent,
  RadioListStyle,
  timeFormatForPrecision,
  TimeInputComponent,
  TimePrecision,
  TimeRangeComponent,
  TimeRangeControl,
  ValidationErrorHintDirective,
} from '@ftr/forms'
import { ButtonComponent, focusElementInContainer, IconButtonComponent, IconLegacyComponent } from '@ftr/foundation'
import { ChronoUnit, LocalTime, ZonedDateTime, ZoneId } from '@js-joda/core'
import { distinctUntilChanged, map, startWith } from 'rxjs'
import { CartTime } from '../../../store'
import { CART_TIME_FORMAT, formatCartTime, ONGOING_END_TIME_PROXY } from '../../../utils'

type OngoingEnd = 'ongoing' | 'custom'

@Component({
  selector: 'ftr-cart-item-form',
  templateUrl: './cart-item-form.component.html',
  styleUrl: './cart-item-form.component.css',
  imports: [
    ButtonComponent,
    IconButtonComponent,
    IconLegacyComponent,
    RadioListComponent,
    TimeInputComponent,
    TimeRangeComponent,
    ValidationErrorHintDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CartItemFormComponent implements OnInit, AfterViewInit {
  private readonly destroyRef = inject(DestroyRef)
  private readonly elementRef = inject(ElementRef)
  private readonly formBuilder = inject(FormBuilder)

  readonly sessionStartTime = input.required<LocalTime>()
  readonly sessionEndTime = input.required<LocalTime | null>()
  readonly requestedTimes = input.required<CartTime[]>()
  readonly courtTimeZoneId = input.required<ZoneId>()

  readonly cancelClicked = output()
  readonly formSubmitted = output<CartTime[]>()

  readonly precision = TimePrecision.Seconds
  readonly timeFormat = timeFormatForPrecision(this.precision)

  formArray: FormArray<TimeRangeControl>
  ongoingEndControl: FormControl<OngoingEnd>
  readonly ongoingEndRadios: RadioItem<OngoingEnd>[] = [
    { key: 'Ongoing', value: 'ongoing' },
    { key: 'Custom end timeframe', value: 'custom' },
  ]
  readonly radioListStyle = RadioListStyle.Inline

  readonly formattedSessionTimes = computed(() =>
    formatCartTime({
      startTime: this.sessionStartTime(),
      endTime: this.sessionEndTime(),
    }),
  )
  readonly courtTimeNow = computed<LocalTime>(() =>
    ZonedDateTime.now(this.courtTimeZoneId()).toLocalTime().truncatedTo(ChronoUnit.SECONDS),
  )
  readonly isOngoingSession = computed<boolean>(() => !this.sessionEndTime())
  readonly availableSessionTimeControl = computed<TimeRangeControl>(
    () =>
      new TimeRangeControl(new LocalTimeRange(this.sessionStartTime(), this.sessionEndTime() ?? this.courtTimeNow())),
  )
  readonly availableOngoingTimeControl = computed<TimeRangeControl>(
    () => new TimeRangeControl(new LocalTimeRange(this.sessionStartTime(), ONGOING_END_TIME_PROXY)),
  )

  ngOnInit(): void {
    this.setUpFormArray()
    this.setUpOngoingEndControl()
  }

  ngAfterViewInit(): void {
    focusElementInContainer(this.elementRef.nativeElement, 'first')
  }

  submitForm(event: SubmitEvent): void {
    event.preventDefault()

    this.formArray.markAllAsTouched()
    if (this.formArray.invalid) return

    const requestedTimes = controlsToCartTimes(this.formArray.controls)
    this.formSubmitted.emit(requestedTimes)
  }

  addTimeframe(): void {
    if (this.lastTimeframeIsOngoing()) {
      this.updateLastControl('custom')
      this.formArray.push(this.buildTimeRangeControl(this.courtTimeNow(), ONGOING_END_TIME_PROXY))
    } else {
      const lastEndTime = this.formArray.controls.at(-1)?.value.end
      this.formArray.push(
        this.buildTimeRangeControl(
          lastEndTime ?? this.sessionStartTime(),
          this.sessionEndTime() ?? this.courtTimeNow(),
        ),
      )
    }
  }

  removeTimeframe(index: number): void {
    if (this.formArray.at(index)) {
      this.formArray.removeAt(index)
    }
  }

  lastTimeframeIsOngoing(): boolean {
    return this.isOngoingSession() && isEndTimeOngoing(this.formArray.controls.at(-1)?.value.end)
  }

  private setUpOngoingEndControl(): void {
    const initialOngoingEnd: OngoingEnd = this.lastTimeframeIsOngoing() ? 'ongoing' : 'custom'
    this.ongoingEndControl = new FormControl<OngoingEnd>(initialOngoingEnd, { nonNullable: true })

    // Whenever the ongoing end radio changes, update the last timeframe control
    this.ongoingEndControl.valueChanges
      .pipe(startWith(initialOngoingEnd), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(ongoingEnd => this.updateLastControl(ongoingEnd))
  }

  private setUpFormArray(): void {
    const controls = this.requestedTimes().map(timeRange =>
      this.buildTimeRangeControl(timeRange.startTime, timeRange.endTime ?? ONGOING_END_TIME_PROXY),
    )
    this.formArray = this.formBuilder.nonNullable.array<TimeRangeControl>(controls)
    this.formArray.addValidators(hasNoOverlappingTimes(this.formArray.controls))

    // Whenever a timeframe is added or removed, check if the ongoing end radio needs to be updated
    this.formArray.valueChanges
      .pipe(
        map(array => array.length),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        const currentOngoingEnd = this.ongoingEndControl.value
        const nextOngoingEnd = this.lastTimeframeIsOngoing() ? 'ongoing' : 'custom'

        if (currentOngoingEnd !== nextOngoingEnd) {
          this.ongoingEndControl.setValue(nextOngoingEnd)
        }
      })
  }

  private buildTimeRangeControl(startTime: LocalTime, endTime: LocalTime): TimeRangeControl {
    const availableTimeControl = isEndTimeOngoing(endTime)
      ? this.availableOngoingTimeControl()
      : this.availableSessionTimeControl()
    const rangeValidators = [isWithinAvailableTime(availableTimeControl)]

    if (isEndTimeOngoing(endTime)) {
      rangeValidators.push(isStartBeforeCourtNow(this.courtTimeNow()))
    }

    return new TimeRangeControl(new LocalTimeRange(startTime, endTime), [Validators.required], rangeValidators)
  }

  private updateLastControl(newOngoingEnd: OngoingEnd): void {
    if (!this.isOngoingSession()) return

    const lastControl = this.formArray.controls.at(-1)
    if (!lastControl) return

    const currentValue = lastControl.value
    const startTime = currentValue.start ?? this.sessionStartTime()

    let endTime: LocalTime
    if (newOngoingEnd === 'ongoing') {
      endTime = ONGOING_END_TIME_PROXY
    } else if (!currentValue.end || isEndTimeOngoing(currentValue.end)) {
      endTime = this.courtTimeNow()
    } else {
      endTime = currentValue.end
    }

    this.formArray.setControl(-1, this.buildTimeRangeControl(startTime, endTime))
  }
}

function isEndTimeOngoing(endTime: LocalTime | null | undefined): boolean {
  if (!endTime) return false
  return endTime.equals(ONGOING_END_TIME_PROXY)
}

function controlsToCartTimes(controls: TimeRangeControl[]): CartTime[] {
  return controls
    .map(control => control.getRawValue())
    .filter(value => isFilledOut(value))
    .map(value => ({ startTime: value.start, endTime: isEndTimeOngoing(value.end) ? null : value.end }))
}

function isWithinAvailableTime(availableTimeControl: TimeRangeControl): ValidatorFn {
  return (control): ValidationErrors | null => {
    const value = control.value
    const availableTime = availableTimeControl.value

    if (isTimeRangeControl(control) && value.start && value.end && availableTime.start && availableTime.end) {
      if (value.start.isBefore(availableTime.start) || value.end.isAfter(availableTime.end)) {
        return {
          outsideAvailableTime: `This timeframe must fit within the available timeframe: ${formatCartTime({ startTime: availableTime.start, endTime: availableTime.end })}`,
        }
      }
      return null
    }

    return null
  }
}

function isStartBeforeCourtNow(courtNow: LocalTime): ValidatorFn {
  return (control): ValidationErrors | null => {
    if (isTimeRangeControl(control)) {
      const value = control.value
      if (!value || !value.start) return null

      if (value.start.isAfter(courtNow)) {
        return {
          startAfterCourtNow: `Start time cannot be after the current time for the court: ${courtNow.format(CART_TIME_FORMAT)}`,
        }
      }
    }

    return null
  }
}

function hasNoOverlappingTimes(controls: TimeRangeControl[]): () => ValidationErrors | null {
  return () => {
    if (controls.length <= 1) return null

    const sortedCartTimes = controlsToCartTimes(controls).sort((a, b) => a.startTime.compareTo(b.startTime))
    let previous: CartTime | null = null
    for (const current of sortedCartTimes) {
      if (previous?.endTime?.isAfter(current.startTime)) {
        return {
          overlapping: `These times overlap: ${formatCartTime(previous)} and ${formatCartTime(current)}`,
        }
      }
      previous = current
    }

    return null
  }
}
